import React from "react";
import { Plus, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

export function DeliverablesPanel({ formData, setFormData }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
            Deliverables
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => setFormData((prev) => ({
              ...prev,
              deliverables: [...prev.deliverables, { id: `del-${Date.now()}`, description: "", delivery_date: "" }],
            }))}
            data-testid="add-deliverable-standalone-btn"
          >
            <Plus size={12} />Add Deliverable
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {formData.deliverables.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No deliverables added yet. Click &quot;Add Deliverable&quot; to start.</p>
        ) : (
          <div className="space-y-2">
            {formData.deliverables.map((del, idx) => (
              <div key={del.id} className="grid grid-cols-[1fr_150px_28px] gap-2 items-start">
                <Input
                  value={del.description}
                  onChange={(e) => {
                    const updated = [...formData.deliverables];
                    updated[idx] = { ...updated[idx], description: e.target.value };
                    setFormData((prev) => ({ ...prev, deliverables: updated }));
                  }}
                  placeholder={`Deliverable ${idx + 1}`}
                  className="rounded-sm h-9"
                  data-testid={`standalone-deliverable-description-${idx}`}
                />
                <Input
                  type="date"
                  value={del.delivery_date}
                  onChange={(e) => {
                    const updated = [...formData.deliverables];
                    updated[idx] = { ...updated[idx], delivery_date: e.target.value };
                    setFormData((prev) => ({ ...prev, deliverables: updated }));
                  }}
                  className="rounded-sm h-9"
                  data-testid={`standalone-deliverable-date-${idx}`}
                />
                <button
                  className="h-9 w-7 flex items-center justify-center text-slate-300 hover:text-red-500"
                  onClick={() => setFormData((prev) => ({
                    ...prev,
                    deliverables: prev.deliverables.filter((_, i) => i !== idx),
                  }))}
                  data-testid={`standalone-remove-deliverable-${idx}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function KpisPanel({ formData, setFormData }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
            KPIs
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => setFormData((prev) => ({
              ...prev,
              kpis: [...prev.kpis, { id: `kpi-${Date.now()}`, description: "", measure: "" }],
            }))}
            data-testid="add-kpi-standalone-btn"
          >
            <Plus size={12} />Add KPI
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {formData.kpis.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No KPIs added yet. Click &quot;Add KPI&quot; to start.</p>
        ) : (
          <div className="space-y-2">
            {formData.kpis.map((kpi, idx) => (
              <div key={kpi.id} className="grid grid-cols-[1fr_1fr_28px] gap-2 items-start">
                <Input
                  value={kpi.description}
                  onChange={(e) => {
                    const updated = [...formData.kpis];
                    updated[idx] = { ...updated[idx], description: e.target.value };
                    setFormData((prev) => ({ ...prev, kpis: updated }));
                  }}
                  placeholder={`KPI ${idx + 1} description`}
                  className="rounded-sm h-9"
                  data-testid={`standalone-kpi-description-${idx}`}
                />
                <Input
                  value={kpi.measure}
                  onChange={(e) => {
                    const updated = [...formData.kpis];
                    updated[idx] = { ...updated[idx], measure: e.target.value };
                    setFormData((prev) => ({ ...prev, kpis: updated }));
                  }}
                  placeholder="e.g., 95% on-time"
                  className="rounded-sm h-9"
                  data-testid={`standalone-kpi-measure-${idx}`}
                />
                <button
                  className="h-9 w-7 flex items-center justify-center text-slate-300 hover:text-red-500"
                  onClick={() => setFormData((prev) => ({
                    ...prev,
                    kpis: prev.kpis.filter((_, i) => i !== idx),
                  }))}
                  data-testid={`standalone-remove-kpi-${idx}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AiAssistantPanel({ aiPrompt, setAiPrompt, aiGenerating, onGenerate, activeSection, sections }) {
  return (
    <Card className="border-cyan-200 dark:border-cyan-800 bg-gradient-to-r from-cyan-50 to-white dark:from-cyan-950/30 dark:to-slate-900">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-mono uppercase tracking-wider text-cyan-700 dark:text-cyan-400 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
          AI Assistant
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          <Input
            placeholder="Describe what you want to draft... (e.g., 'Add payment terms for monthly billing')"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onGenerate(activeSection)}
            className="flex-1 rounded-sm"
            data-testid="ai-prompt-input"
          />
          <Button
            onClick={() => onGenerate(activeSection)}
            disabled={aiGenerating || !aiPrompt}
            className="gap-2 rounded-sm bg-cyan-600 hover:bg-cyan-700"
            data-testid="ai-generate-btn"
          >
            {aiGenerating ? (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
            )}
            Generate
          </Button>
        </div>
        {activeSection && (
          <p className="text-xs text-cyan-600 mt-2">
            Generating for: {sections.find((s) => s.id === activeSection)?.name}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
